import type { ReactElement } from 'react';
import { Badge, Box, Group, Stack, Text } from '@mantine/core';
import { ArrowLeftRight, Scale, Sparkles } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import { accentTokens, type VizAccent } from '@/design/VizAccents';
import { formatUsd } from '@/shared/analytics/MetricFormatting';
import type { Recommendation } from './CostRecommendations';

interface SpendCostRecommendationsPanelProps {
    recommendations: Recommendation[];
    accent: VizAccent;
}

/** Footing (license vs API) and routing (cheaper-model) recommendations as a card with per-item icon, detail, and a monthly $-impact badge. Colours follow the page accent. */
export default function SpendCostRecommendationsPanel({ recommendations, accent }: SpendCostRecommendationsPanelProps): ReactElement {
    const tokens = accentTokens(accent);
    return (
        <SurfaceCard $padding="md">
            <Group gap={6} mb="sm" wrap="nowrap">
                <Sparkles size={14} style={{ color: tokens.text }} />
                <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                    Recommendations
                </Text>
            </Group>

            {recommendations.length === 0 ?
                <Text size="sm" c="dimmed">
                    No cost-saving recommendations at the current run rate.
                </Text>
            :   <Stack gap="sm">
                    {recommendations.map((rec) => {
                        const Icon = rec.kind === 'footing' ? Scale : ArrowLeftRight;
                        return (
                            <Group key={rec.id} gap="sm" wrap="nowrap" align="flex-start">
                                <Box mt={2} style={{ color: tokens.text, flexShrink: 0 }}>
                                    <Icon size={16} />
                                </Box>
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Group justify="space-between" gap="sm" wrap="nowrap" align="baseline">
                                        <Text size="sm" fw={600}>
                                            {rec.title}
                                        </Text>
                                        <Badge size="sm" variant="light" style={{ flexShrink: 0, backgroundColor: tokens.soft, color: tokens.text }}>
                                            ~{formatUsd(rec.monthlyImpactUsd)}/mo
                                        </Badge>
                                    </Group>
                                    <Text size="xs" c="dimmed" mt={2}>
                                        {rec.detail}
                                    </Text>
                                </Box>
                            </Group>
                        );
                    })}
                </Stack>
            }
        </SurfaceCard>
    );
}
